import type { ImageDataLike } from './sample.js';

/**
 * Pull pixels out of a live video element or a still image.
 */

/** A reusable canvas, so grabbing frames does not allocate one per capture. */
let scratch: { canvas: HTMLCanvasElement; context: CanvasRenderingContext2D } | null = null;

function getScratchCanvas(width: number, height: number) {
  if (!scratch) {
    const canvas = document.createElement('canvas');
    // willReadFrequently tells the browser to keep this canvas on the CPU;
    // without it, every getImageData call forces a GPU readback stall.
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Could not acquire a 2D canvas context.');
    scratch = { canvas, context };
  }
  if (scratch.canvas.width !== width || scratch.canvas.height !== height) {
    scratch.canvas.width = width;
    scratch.canvas.height = height;
  }
  return scratch;
}

/**
 * Capture the current video frame at the stream's native resolution.
 *
 * Native resolution matters: sampling from a CSS-scaled copy would average in
 * whatever interpolation the browser applied on the way to the screen.
 */
export function captureFrame(video: HTMLVideoElement): ImageData {
  const width = video.videoWidth;
  const height = video.videoHeight;
  if (width === 0 || height === 0) {
    throw new Error('The video has no frame yet. Wait for its "loadeddata" event.');
  }

  const { context } = getScratchCanvas(width, height);
  context.drawImage(video, 0, 0, width, height);
  return context.getImageData(0, 0, width, height);
}

/** Read a still image into pixel data. Used for testing against fixture photos. */
export function captureImage(image: HTMLImageElement): ImageData {
  const width = image.naturalWidth;
  const height = image.naturalHeight;
  if (width === 0 || height === 0) {
    throw new Error('The image has not finished loading.');
  }

  const { context } = getScratchCanvas(width, height);
  context.drawImage(image, 0, 0, width, height);
  return context.getImageData(0, 0, width, height);
}

export class ImageDecodeError extends Error {
  constructor(message: string, cause?: unknown) {
    super(message, { cause });
    this.name = 'ImageDecodeError';
  }
}

/**
 * Decode a user-supplied image file to pixels at full resolution.
 *
 * `imageOrientation: 'from-image'` is load-bearing. Phone photographs are
 * almost always stored in the sensor's native orientation with an EXIF tag
 * saying how to rotate them; ignoring it silently transposes the image, and
 * every sample coordinate would then point somewhere else entirely.
 *
 * Decoding goes through the browser's own image pipeline, so format support is
 * whatever the browser has. Notably that excludes HEIC everywhere except
 * Safari — the failure is reported rather than swallowed, because "nothing
 * happened" is the worst possible response to an unsupported file.
 */
export async function decodeImageFile(file: Blob): Promise<ImageData> {
  if (typeof createImageBitmap !== 'function') {
    throw new ImageDecodeError('This browser cannot decode image files.');
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch (error) {
    const type = file instanceof File ? file.name : file.type;
    throw new ImageDecodeError(
      `Could not decode "${type}". Browsers other than Safari cannot read HEIC — ` +
        'export or re-shoot as JPEG (Settings → Camera → Formats → Most Compatible).',
      error,
    );
  }

  try {
    const { context } = getScratchCanvas(bitmap.width, bitmap.height);
    context.drawImage(bitmap, 0, 0);
    return context.getImageData(0, 0, bitmap.width, bitmap.height);
  } finally {
    bitmap.close();
  }
}

/**
 * Side length for a sample region, as a fraction of frame width.
 *
 * Expressed proportionally so that a 12 MP still and a 1080p video preview
 * sample a comparable *physical* area of the subject. A fixed pixel count
 * would sample four times less of the scene on the still.
 */
export function sampleSizeFor(frameWidth: number): number {
  return Math.max(9, Math.min(81, Math.round(frameWidth * 0.007)));
}

/**
 * Map a pointer position on a displayed element to pixel coordinates in the
 * source frame.
 *
 * `object-fit: contain` letterboxes the video, so the displayed area is not the
 * element's box. Getting this wrong puts every sample slightly off-target,
 * which is invisible on a flat swatch and badly wrong near an edge.
 */
export function clientPointToFrame(
  element: HTMLElement,
  clientX: number,
  clientY: number,
  frameWidth: number,
  frameHeight: number,
): { x: number; y: number } | null {
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return null;

  const scale = Math.min(rect.width / frameWidth, rect.height / frameHeight);
  const renderedWidth = frameWidth * scale;
  const renderedHeight = frameHeight * scale;
  const offsetX = (rect.width - renderedWidth) / 2;
  const offsetY = (rect.height - renderedHeight) / 2;

  const x = (clientX - rect.left - offsetX) / scale;
  const y = (clientY - rect.top - offsetY) / scale;

  // A tap in the letterbox bars is outside the image and has no valid pixel.
  if (x < 0 || y < 0 || x >= frameWidth || y >= frameHeight) return null;

  return { x, y };
}

export type { ImageDataLike };
